import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import ErrorModal from "../../utils/ErrorModal";
// If this file is one folder deeper (e.g. components/Admin/), change to "../../utils/ErrorModal"

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const headCellSx = {
  py: 1.25,
  px: 2,
  background: "#F7F9FC",
  borderBottom: `1px solid ${LINE}`,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: MUTED,
  "& strong": { fontWeight: 700 },
};
const bodyCellSx = {
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #F1F4F8",
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
};

const LeadSourceForProject = ({ project }) => {
  const [source, setSource] = useState("");
  const [statusList, setStatusList] = useState();
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const isMobile = useMediaQuery("(max-width: 750px)");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type) => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead-source?projectId=${project.projectId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setStatusList(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleAdd = async () => {
    if (!source.trim()) return;
    try {
      setBtnLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead-source?source=${source}&projectId=${project.projectId}`,
        {},
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      fetchStatus();
      showModal("Lead source added", "success");
      setStatusList((prev) => [...prev, source.trim()]);
    } catch (error) {
      showModal(error?.response?.data?.Exception || "Error while adding status", "error");
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
    setSource("");
  };

  return (
    <Box py={2} display="flex" flexDirection="column" gap={2} sx={{ fontFamily: FONT, color: INK }}>
      <Typography
        variant="h5"
        my={1}
        fontWeight={700}
        textAlign={"center"}
        sx={{ fontFamily: FONT, fontSize: { xs: 22, sm: 24 }, fontWeight: 800, letterSpacing: "-0.025em", color: INK, mb: 0 }}
      >
        Manage Lead Sources
      </Typography>

      <Box
        className="add-forms"
        sx={{
          width: "100%",
          boxSizing: "border-box",
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          p: "16px 18px",
          m: 0,
          boxShadow: "none",
        }}
      >
        <Grid container spacing={1.5} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 5 }}>
            <label
              htmlFor=""
              style={{ display: "block", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK_2 }}
            >
              Lead Source
            </label>
            <input
              type="text"
              placeholder="Enter lead source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              onKeyDown={undefined}
              style={{
                flex: 1,
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                height: 40,
                padding: "0 12px",
                margin: 0,
                background: "#FFFFFF",
                border: `1px solid ${FIELD_EDGE}`,
                borderRadius: "9px",
                fontFamily: FONT,
                fontSize: 14,
                color: INK,
                outline: "none",
              }}
            />
          </Grid>

          <Grid
            size={{ xs: 12, sm: 3 }}
            sx={{
              display: { xs: "block", sm: "flex" },
              justifyContent: "flex-start",
              alignItems: "flex-end",
            }}
          >
            <Button
              className="btn-contained"
              fullWidth
              onClick={handleAdd}
              sx={{
                marginTop: "0 !important",
                height: 40,
                borderRadius: "9px !important",
                background: `${PRIMARY} !important`,
                color: "#FFFFFF !important",
                border: "0 !important",
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: "#084767 !important" },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
              disabled={btnLoading}
            >
              Add Source
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isMobile ? (
        <Box
          sx={{
            background: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          {statusList?.map((s) => (
            <div
              key={s.sourceId}
              className="card"
              style={{
                margin: 0,
                padding: "12px 14px",
                background: "transparent",
                border: 0,
                borderBottom: "1px solid #F1F4F8",
                borderRadius: 0,
                boxShadow: "none",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <Typography
                    fontSize={"15px"}
                    color={INK}
                    fontWeight={"700"}
                    sx={{ fontFamily: FONT, letterSpacing: "-0.01em" }}
                  >
                    {s.source}
                  </Typography>
                  <Typography
                    fontSize={"12px"}
                    color={MUTED}
                    fontWeight={"500"}
                    sx={{ fontFamily: MONO, mt: 0.25 }}
                  >
                    ID: {s.sourceId}
                  </Typography>
                </div>
              </div>

              <hr style={{ display: "none" }} />
            </div>
          ))}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            background: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 72 }}>
                  <strong>#</strong>
                </TableCell>
                <TableCell sx={headCellSx}>
                  <strong>Source</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {statusList?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} sx={{ ...bodyCellSx, color: MUTED, py: 2.5, borderBottom: 0 }}>
                    No sources added yet.
                  </TableCell>
                </TableRow>
              ) : (
                statusList?.map((item, index) => (
                  <TableRow key={index} sx={{ "&:hover": { background: "#F4F7FA" }, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ ...bodyCellSx, fontFamily: MONO, fontSize: 12.5, color: MUTED }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{item.source}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />
    </Box>
  );
};

export default LeadSourceForProject;